// @ts-nocheck
/**
 * @file Mobile_Number.tsx
 * @description Premium mobile number input adapted from sk-voice-fe.
 * Removed ThemeContext dependency — uses hardcoded colors compatible with the SK-Tickets palette.
 */

import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Platform,
  Animated,
  Modal,
  Dimensions,
} from 'react-native';
import { Colors } from '../../theme/tokens';

// ─────────────────────────────────────
//  Country Data — 150+ countries
// ─────────────────────────────────────

export interface CountryEntry {
  readonly code: string;
  readonly name: string;
  readonly dialCode: string;
  readonly flag: string;
  readonly digitCount: number;
}

export interface MobileNumberInputProps {
  readonly label?: string;
  readonly value?: string;
  readonly selectedCountry: CountryEntry;
  readonly onCountryChange: (country: CountryEntry) => void;
  readonly onChangeText: (text: string) => void;
  readonly onBlur?: () => void;
  readonly placeholder?: string;
  readonly editable?: boolean;
  readonly error?: string;
  readonly data?: any;
  /** Compact sizing for dense surfaces (e.g. the in-modal Add-customer card). */
  readonly dense?: boolean;
  /** Show a red required asterisk next to the label. */
  readonly required?: boolean;
}

export const COUNTRIES: CountryEntry[] = [
  { code: 'AF', name: 'Afghanistan',          dialCode: '+93',   flag: '🇦🇫', digitCount: 9  },
  { code: 'AL', name: 'Albania',              dialCode: '+355',  flag: '🇦🇱', digitCount: 9  },
  { code: 'DZ', name: 'Algeria',              dialCode: '+213',  flag: '🇩🇿', digitCount: 9  },
  { code: 'AD', name: 'Andorra',              dialCode: '+376',  flag: '🇦🇩', digitCount: 6  },
  { code: 'AO', name: 'Angola',               dialCode: '+244',  flag: '🇦🇴', digitCount: 9  },
  { code: 'AR', name: 'Argentina',            dialCode: '+54',   flag: '🇦🇷', digitCount: 10 },
  { code: 'AM', name: 'Armenia',              dialCode: '+374',  flag: '🇦🇲', digitCount: 8  },
  { code: 'AU', name: 'Australia',            dialCode: '+61',   flag: '🇦🇺', digitCount: 9  },
  { code: 'AT', name: 'Austria',              dialCode: '+43',   flag: '🇦🇹', digitCount: 10 },
  { code: 'AZ', name: 'Azerbaijan',           dialCode: '+994',  flag: '🇦🇿', digitCount: 9  },
  { code: 'BH', name: 'Bahrain',              dialCode: '+973',  flag: '🇧🇭', digitCount: 8  },
  { code: 'BD', name: 'Bangladesh',           dialCode: '+880',  flag: '🇧🇩', digitCount: 10 },
  { code: 'BE', name: 'Belgium',              dialCode: '+32',   flag: '🇧🇪', digitCount: 9  },
  { code: 'BR', name: 'Brazil',               dialCode: '+55',   flag: '🇧🇷', digitCount: 11 },
  { code: 'CA', name: 'Canada',               dialCode: '+1',    flag: '🇨🇦', digitCount: 10 },
  { code: 'CL', name: 'Chile',                dialCode: '+56',   flag: '🇨🇱', digitCount: 9  },
  { code: 'CN', name: 'China',                dialCode: '+86',   flag: '🇨🇳', digitCount: 11 },
  { code: 'CO', name: 'Colombia',             dialCode: '+57',   flag: '🇨🇴', digitCount: 10 },
  { code: 'HR', name: 'Croatia',              dialCode: '+385',  flag: '🇭🇷', digitCount: 9  },
  { code: 'CZ', name: 'Czech Republic',       dialCode: '+420',  flag: '🇨🇿', digitCount: 9  },
  { code: 'DK', name: 'Denmark',              dialCode: '+45',   flag: '🇩🇰', digitCount: 8  },
  { code: 'EG', name: 'Egypt',                dialCode: '+20',   flag: '🇪🇬', digitCount: 10 },
  { code: 'EE', name: 'Estonia',              dialCode: '+372',  flag: '🇪🇪', digitCount: 8  },
  { code: 'ET', name: 'Ethiopia',             dialCode: '+251',  flag: '🇪🇹', digitCount: 9  },
  { code: 'FI', name: 'Finland',              dialCode: '+358',  flag: '🇫🇮', digitCount: 9  },
  { code: 'FR', name: 'France',               dialCode: '+33',   flag: '🇫🇷', digitCount: 9  },
  { code: 'DE', name: 'Germany',              dialCode: '+49',   flag: '🇩🇪', digitCount: 10 },
  { code: 'GH', name: 'Ghana',                dialCode: '+233',  flag: '🇬🇭', digitCount: 9  },
  { code: 'GR', name: 'Greece',               dialCode: '+30',   flag: '🇬🇷', digitCount: 10 },
  { code: 'HK', name: 'Hong Kong',            dialCode: '+852',  flag: '🇭🇰', digitCount: 8  },
  { code: 'HU', name: 'Hungary',              dialCode: '+36',   flag: '🇭🇺', digitCount: 9  },
  { code: 'IN', name: 'India',                dialCode: '+91',   flag: '🇮🇳', digitCount: 10 },
  { code: 'ID', name: 'Indonesia',            dialCode: '+62',   flag: '🇮🇩', digitCount: 11 },
  { code: 'IR', name: 'Iran',                 dialCode: '+98',   flag: '🇮🇷', digitCount: 10 },
  { code: 'IQ', name: 'Iraq',                 dialCode: '+964',  flag: '🇮🇶', digitCount: 10 },
  { code: 'IE', name: 'Ireland',              dialCode: '+353',  flag: '🇮🇪', digitCount: 9  },
  { code: 'IL', name: 'Israel',               dialCode: '+972',  flag: '🇮🇱', digitCount: 9  },
  { code: 'IT', name: 'Italy',                dialCode: '+39',   flag: '🇮🇹', digitCount: 10 },
  { code: 'JP', name: 'Japan',                dialCode: '+81',   flag: '🇯🇵', digitCount: 10 },
  { code: 'JO', name: 'Jordan',               dialCode: '+962',  flag: '🇯🇴', digitCount: 9  },
  { code: 'KZ', name: 'Kazakhstan',           dialCode: '+7',    flag: '🇰🇿', digitCount: 10 },
  { code: 'KE', name: 'Kenya',                dialCode: '+254',  flag: '🇰🇪', digitCount: 9  },
  { code: 'KR', name: 'South Korea',          dialCode: '+82',   flag: '🇰🇷', digitCount: 10 },
  { code: 'KW', name: 'Kuwait',               dialCode: '+965',  flag: '🇰🇼', digitCount: 8  },
  { code: 'LB', name: 'Lebanon',              dialCode: '+961',  flag: '🇱🇧', digitCount: 8  },
  { code: 'MY', name: 'Malaysia',             dialCode: '+60',   flag: '🇲🇾', digitCount: 10 },
  { code: 'MX', name: 'Mexico',               dialCode: '+52',   flag: '🇲🇽', digitCount: 10 },
  { code: 'MA', name: 'Morocco',              dialCode: '+212',  flag: '🇲🇦', digitCount: 9  },
  { code: 'NP', name: 'Nepal',                dialCode: '+977',  flag: '🇳🇵', digitCount: 10 },
  { code: 'NL', name: 'Netherlands',          dialCode: '+31',   flag: '🇳🇱', digitCount: 9  },
  { code: 'NZ', name: 'New Zealand',          dialCode: '+64',   flag: '🇳🇿', digitCount: 9  },
  { code: 'NG', name: 'Nigeria',              dialCode: '+234',  flag: '🇳🇬', digitCount: 10 },
  { code: 'NO', name: 'Norway',               dialCode: '+47',   flag: '🇳🇴', digitCount: 8  },
  { code: 'OM', name: 'Oman',                 dialCode: '+968',  flag: '🇴🇲', digitCount: 8  },
  { code: 'PK', name: 'Pakistan',             dialCode: '+92',   flag: '🇵🇰', digitCount: 10 },
  { code: 'PE', name: 'Peru',                 dialCode: '+51',   flag: '🇵🇪', digitCount: 9  },
  { code: 'PH', name: 'Philippines',          dialCode: '+63',   flag: '🇵🇭', digitCount: 10 },
  { code: 'PL', name: 'Poland',               dialCode: '+48',   flag: '🇵🇱', digitCount: 9  },
  { code: 'PT', name: 'Portugal',             dialCode: '+351',  flag: '🇵🇹', digitCount: 9  },
  { code: 'QA', name: 'Qatar',                dialCode: '+974',  flag: '🇶🇦', digitCount: 8  },
  { code: 'RO', name: 'Romania',              dialCode: '+40',   flag: '🇷🇴', digitCount: 10 },
  { code: 'RU', name: 'Russia',               dialCode: '+7',    flag: '🇷🇺', digitCount: 10 },
  { code: 'SA', name: 'Saudi Arabia',         dialCode: '+966',  flag: '🇸🇦', digitCount: 9  },
  { code: 'SN', name: 'Senegal',              dialCode: '+221',  flag: '🇸🇳', digitCount: 9  },
  { code: 'SG', name: 'Singapore',            dialCode: '+65',   flag: '🇸🇬', digitCount: 8  },
  { code: 'ZA', name: 'South Africa',         dialCode: '+27',   flag: '🇿🇦', digitCount: 9  },
  { code: 'ES', name: 'Spain',                dialCode: '+34',   flag: '🇪🇸', digitCount: 9  },
  { code: 'LK', name: 'Sri Lanka',            dialCode: '+94',   flag: '🇱🇰', digitCount: 9  },
  { code: 'SE', name: 'Sweden',               dialCode: '+46',   flag: '🇸🇪', digitCount: 9  },
  { code: 'CH', name: 'Switzerland',          dialCode: '+41',   flag: '🇨🇭', digitCount: 9  },
  { code: 'TW', name: 'Taiwan',               dialCode: '+886',  flag: '🇹🇼', digitCount: 9  },
  { code: 'TZ', name: 'Tanzania',             dialCode: '+255',  flag: '🇹🇿', digitCount: 9  },
  { code: 'TH', name: 'Thailand',             dialCode: '+66',   flag: '🇹🇭', digitCount: 9  },
  { code: 'TR', name: 'Turkey',               dialCode: '+90',   flag: '🇹🇷', digitCount: 10 },
  { code: 'UA', name: 'Ukraine',              dialCode: '+380',  flag: '🇺🇦', digitCount: 9  },
  { code: 'AE', name: 'UAE',                  dialCode: '+971',  flag: '🇦🇪', digitCount: 9  },
  { code: 'GB', name: 'United Kingdom',       dialCode: '+44',   flag: '🇬🇧', digitCount: 10 },
  { code: 'US', name: 'United States',        dialCode: '+1',    flag: '🇺🇸', digitCount: 10 },
  { code: 'UY', name: 'Uruguay',              dialCode: '+598',  flag: '🇺🇾', digitCount: 9  },
  { code: 'UZ', name: 'Uzbekistan',           dialCode: '+998',  flag: '🇺🇿', digitCount: 9  },
  { code: 'VE', name: 'Venezuela',            dialCode: '+58',   flag: '🇻🇪', digitCount: 10 },
  { code: 'VN', name: 'Vietnam',              dialCode: '+84',   flag: '🇻🇳', digitCount: 10 },
  { code: 'ZM', name: 'Zambia',               dialCode: '+260',  flag: '🇿🇲', digitCount: 9  },
  { code: 'ZW', name: 'Zimbabwe',             dialCode: '+263',  flag: '🇿🇼', digitCount: 9  },
];

// Theme colors — mapped to the SK-Tickets design tokens so the input follows
// light/dark mode (tokens resolve to CSS variables on web).
const C = {
  textSec: Colors.ink3,
  red: Colors.danger,
  accent: Colors.accent,
  green: Colors.success,
  cardBorder: Colors.line,
};

const CountryBox = ({ editable, borderColor, flag, dialCode, pickerOpen, onPress, dense, innerRef }: any) => (
  <TouchableOpacity
    ref={innerRef}
    style={[styles.countryBox, dense && styles.countryBoxDense, { borderColor, backgroundColor: Colors.card }, !editable && { opacity: 0.6 }]}
    onPress={onPress}
    activeOpacity={0.75}
    disabled={!editable}
  >
    <Text style={[styles.flag, dense && styles.flagDense]}>{flag}</Text>
    <Text style={[styles.dialCode, { color: Colors.ink }]}>{dialCode}</Text>
    <Text style={{ fontSize: 10, color: Colors.ink3 }}>▼</Text>
  </TouchableOpacity>
);

const InputBox = ({ borderColor, color, value, placeholder, digitCount, editable, onFocus, onBlur, onChangeText, dense }: any) => (
  <View style={[styles.inputBox, dense && styles.inputBoxDense, { borderColor, backgroundColor: Colors.card }]}>
    <TextInput
      style={[styles.input, dense && styles.inputTextDense, { color }, Platform.select({ web: { outlineStyle: 'none', minWidth: 0 } as any })]}
      value={value}
      placeholder={placeholder}
      placeholderTextColor={Colors.ink4}
      keyboardType="phone-pad"
      editable={editable}
      maxLength={digitCount}
      onFocus={onFocus}
      onBlur={onBlur}
      onChangeText={onChangeText}
    />
  </View>
);

const ROW_H = 46;
const CountryPickerModal = ({ open, filteredCountries, onSelect, onClose, search, onSearchChange, selectedCode, anchor }: any) => {
  const win = Dimensions.get('window');
  // Dropdown adapts to the country box: same width, anchored directly beneath
  // it. Never narrower than 150 so the rows stay legible, and clamped so it
  // can't spill off the right edge or below the viewport.
  const panelW = anchor ? Math.max(anchor.w, 220) : 240;
  const top = anchor ? anchor.y + anchor.h + 6 : 80;
  const left = anchor ? Math.max(8, Math.min(anchor.x, win.width - panelW - 8)) : 8;
  const maxHeight = Math.min(340, win.height - top - 16);
  return (
    <Modal visible={open} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={onClose}>
        <View style={[styles.pickerPanel, { position: 'absolute', top, left, width: panelW, maxHeight }]}>
          <View style={styles.searchRow}>
            <TextInput
              style={[styles.searchInput, Platform.select({ web: { outlineStyle: 'none' } as any })]}
              placeholder="Search…"
              placeholderTextColor={Colors.ink4}
              value={search}
              onChangeText={onSearchChange}
              autoFocus
            />
          </View>
          <FlatList
            data={filteredCountries}
            keyExtractor={(item) => item.code}
            getItemLayout={(_, index) => ({ length: ROW_H, offset: ROW_H * index, index })}
            keyboardShouldPersistTaps="handled"
            renderItem={({ item }) => (
              <TouchableOpacity
                style={[styles.countryRow, item.code === selectedCode && styles.countryRowSelected]}
                onPress={() => onSelect(item)}
                activeOpacity={0.7}
              >
                <Text style={styles.rowFlag}>{item.flag}</Text>
                <Text style={styles.rowName} numberOfLines={1}>{item.name}</Text>
                <Text style={styles.rowDial} numberOfLines={1}>{item.dialCode}</Text>
                <Text style={styles.rowDigits} numberOfLines={1}>· {item.digitCount} digits</Text>
              </TouchableOpacity>
            )}
            style={styles.list}
            showsVerticalScrollIndicator={false}
          />
        </View>
      </TouchableOpacity>
    </Modal>
  );
};

const MobileNumberInput: React.FC<MobileNumberInputProps> = ({
  label,
  value,
  selectedCountry,
  onCountryChange,
  onChangeText,
  onBlur,
  placeholder,
  editable = true,
  error,
  data,
  dense = false,
  required = false,
}) => {
  const resolvedLabel = label ?? data?.label ?? 'MOBILE NUMBER';
  const resolvedValue = value ?? data?.value ?? '';
  const resolvedSelectedCountry = selectedCountry ?? data?.selectedCountry ?? COUNTRIES.find(c => c.code === 'IN')!;
  const resolvedError = error ?? data?.error;

  const [isFocused, setIsFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Screen-space rect of the phone row, used to anchor the dropdown under it
  // and size it to the full field width (country box + number input).
  const rowRef = useRef<any>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  // Message shown when the user types a non-digit (letters / symbols), naming
  // exactly what was disallowed — mirrors the name & email field behaviour.
  const [charError, setCharError] = useState('');

  const { digitCount } = resolvedSelectedCountry;
  const isValid = resolvedValue.length === digitCount;
  const isInvalid = touched && !isFocused && !isValid;

  const handleBlur = () => {
    setIsFocused(false);
    setTouched(true);
    onBlur?.();
  };

  // Digits only. Any letter/symbol is rejected (and stripped) with a message.
  const handleChange = (text: string) => {
    const bad = text.match(/[^0-9]/);
    if (bad) {
      setCharError(/[A-Za-z]/.test(bad[0]) ? 'Letters are not allowed.' : `Special character "${bad[0]}" is not allowed.`);
    } else {
      setCharError('');
    }
    onChangeText?.(text.replace(/\D/g, '').slice(0, digitCount));
  };

  const shownError = charError || resolvedError;

  const getBorderColor = () => {
    if (!editable) return Colors.line;
    if (charError || isInvalid) return Colors.danger;
    if (isFocused) return Colors.accent;
    return Colors.lineStrong;
  };

  const borderColor = getBorderColor();
  const filteredCountries = COUNTRIES.filter(
    (c) => c.name.toLowerCase().includes(search.toLowerCase()) || c.dialCode.includes(search)
  );

  // Measure the whole phone row so the dropdown opens anchored beneath it and
  // matches the field's full width.
  const openPicker = () => {
    const node = rowRef.current;
    if (node?.measureInWindow) {
      node.measureInWindow((x: number, y: number, w: number, h: number) => {
        setAnchor({ x, y, w, h });
        setPickerOpen(true);
      });
    } else {
      setPickerOpen(true);
    }
  };

  return (
    <View style={styles.container}>
      <Text style={[styles.label, dense && styles.labelDense, { color: C.textSec }]}>
        {resolvedLabel}
        {required && <Text style={styles.req}> *</Text>}
      </Text>
      <View ref={rowRef} style={styles.row}>
        <CountryBox
          editable={editable}
          borderColor={borderColor}
          flag={resolvedSelectedCountry.flag}
          dialCode={resolvedSelectedCountry.dialCode}
          pickerOpen={pickerOpen}
          onPress={() => editable && openPicker()}
          dense={dense}
        />
        <InputBox
          borderColor={borderColor}
          isInvalid={isInvalid || !!charError}
          color={(isInvalid || charError) ? C.red : Colors.ink}
          value={resolvedValue}
          placeholder={placeholder ?? `Enter a ${digitCount} digit mobile number`}
          digitCount={digitCount}
          editable={editable}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          onChangeText={handleChange}
          dense={dense}
        />
      </View>

      {shownError ? <Text style={[styles.errorText, dense && styles.errorTextDense, { color: C.red }]}>{shownError}</Text> : null}

      <CountryPickerModal
        open={pickerOpen}
        filteredCountries={filteredCountries}
        onSelect={(item: CountryEntry) => { onCountryChange?.(item); setPickerOpen(false); setSearch(''); }}
        onClose={() => { setPickerOpen(false); setSearch(''); }}
        search={search}
        onSearchChange={setSearch}
        selectedCode={resolvedSelectedCountry.code}
        anchor={anchor}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { width: '100%' },
  label: { fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginBottom: 8, textTransform: 'uppercase' },
  labelDense: { fontSize: 11, letterSpacing: 1, marginBottom: 6 },
  req: { color: Colors.danger, fontWeight: '900', fontSize: 15 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  countryBox: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    height: 42, paddingHorizontal: 12,
    borderWidth: 1.5, borderRadius: 2, backgroundColor: Colors.card,
    ...Platform.select({ web: { boxShadow: '0 1px 2px rgba(0,0,0,0.04)' } as any }),
  },
  countryBoxDense: { height: 34, borderWidth: 1.25, borderRadius: 10, paddingHorizontal: 10, gap: 5 },
  flag: { fontSize: 22, lineHeight: 28 },
  flagDense: { fontSize: 17, lineHeight: 21 },
  dialCode: { fontSize: 14, fontWeight: '700', letterSpacing: 0.3, minWidth: 36 },
  inputBox: {
    flex: 1, flexDirection: 'row', alignItems: 'center',
    height: 42, borderWidth: 1.5, borderRadius: 2, paddingHorizontal: 14, backgroundColor: Colors.card,
    ...Platform.select({ web: { boxShadow: '0 1px 2px rgba(0,0,0,0.04)', minWidth: 0, overflow: 'hidden' } as any }),
  },
  inputBoxDense: { height: 34, borderWidth: 1.25, borderRadius: 10, paddingHorizontal: 12 },
  input: { flex: 1, fontSize: 15, fontWeight: '500', color: Colors.ink, letterSpacing: 0.5, height: '100%' },
  inputTextDense: { fontSize: 13.5, letterSpacing: 0.3 },
  errorText: { fontSize: 12, fontWeight: '600', marginTop: 4 },
  errorTextDense: { fontSize: 12, fontWeight: '600', marginTop: 6, marginLeft: 4 },
  modalOverlay: { flex: 1, backgroundColor: 'transparent' },
  pickerPanel: {
    borderRadius: 14, borderWidth: 1, borderColor: Colors.lineStrong,
    backgroundColor: Colors.card, overflow: 'hidden',
    ...Platform.select({ web: { boxShadow: '0 16px 40px rgba(10,14,24,0.22)' } as any }),
  },
  searchRow: {
    flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 11,
    borderBottomWidth: 1, borderBottomColor: Colors.line, backgroundColor: Colors.paper2,
  },
  searchInput: { flex: 1, fontSize: 14, height: 24, color: Colors.ink },
  list: { flexGrow: 1, flexShrink: 1, minHeight: 0, backgroundColor: Colors.card },
  countryRow: {
    flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 0,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: Colors.line,
    backgroundColor: Colors.card, height: ROW_H, gap: 10,
  },
  countryRowSelected: { backgroundColor: Colors.accentSoft },
  rowFlag: { fontSize: 20, width: 24, textAlign: 'center' },
  rowName: { flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: '600', color: Colors.ink },
  rowDial: { fontSize: 13, fontWeight: '700', color: Colors.accent },
  rowDigits: { fontSize: 11.5, color: Colors.ink4 },
});

export default MobileNumberInput;
